if (!customElements.get('card-quantity-add')) {
  customElements.define(
    'card-quantity-add',
    class CardQuantityAdd extends HTMLElement {
      constructor() {
        super();

        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

        this.form = this.querySelector('form');
        this.idInput = this.querySelector('[name="id"]');
        this.qtyInput = this.querySelector('.card-qty__input');
        this.minusButton = this.querySelector('[data-card-qty="minus"]');
        this.plusButton = this.querySelector('[data-card-qty="plus"]');
        this.submitButton = this.querySelector('[name="add"]');
        this.submitButtonText = this.submitButton?.querySelector('.card-add__text');
        this.variantSelect = this.querySelector('.card-variant__native');
        this.moqPill = this.querySelector('[data-card-moq-pill]');

        try {
          const data = this.querySelector('[data-card-variants]');
          this.variants = data ? JSON.parse(data.textContent) : null;
        } catch (e) {
          this.variants = null;
        }

        this.hasVariants = !!this.variantSelect;

        this.minusButton?.addEventListener('click', this.onMinus.bind(this));
        this.plusButton?.addEventListener('click', this.onPlus.bind(this));
        this.qtyInput?.addEventListener('change', this.onQtyChange.bind(this));
        this.variantSelect?.addEventListener('change', this.onVariantChange.bind(this));
        this.form?.addEventListener('submit', this.onSubmit.bind(this));

        if (this.hasVariants) {
          this.onVariantChange();
        } else {
          this.applyMoq(this.currentMoq());
          this.updateStepButtons();
          this.updatePill(this.currentMoq());
        }

        this.exposeClickArea();
      }

      exposeClickArea() {
        const gridItem = this.closest('.grid__item') || this.closest('li');
        if (!gridItem) return;

        const setHeight = () => {
          const rect = this.getBoundingClientRect();
          if (!rect.height) return;

          const style = getComputedStyle(this);
          const marginTop = parseFloat(style.marginTop) || 0;
          gridItem.style.setProperty('--card-add-height', `${Math.ceil(rect.height + marginTop) + 4}px`);
        };

        setHeight();
        requestAnimationFrame(setHeight);
        if ('ResizeObserver' in window) {
          this.resizeObserver = new ResizeObserver(setHeight);
          this.resizeObserver.observe(this);
        } else {
          window.addEventListener('resize', setHeight);
        }
      }

      disconnectedCallback() {
        if (this.resizeObserver) this.resizeObserver.disconnect();
      }

      currentMoq() {
        let moq;
        if (this.hasVariants) {
          const selected = this.selectedVariant();
          moq = selected ? parseInt(selected.moq, 10) : 1;
        } else {
          moq = parseInt(this.dataset.moq, 10);
        }
        if (!moq || moq < 1) moq = 1;
        return moq;
      }

      selectedVariant() {
        if (!this.variants || !this.variantSelect || !this.variantSelect.value) return null;
        return this.variants.find((v) => String(v.id) === String(this.variantSelect.value)) || null;
      }

      applyMoq(moq) {
        if (!this.qtyInput) return;
        this.qtyInput.min = moq;
        this.qtyInput.step = moq;
        this.qtyInput.value = moq;
      }

      onMinus(event) {
        event.preventDefault();
        const moq = this.currentMoq();
        const value = parseInt(this.qtyInput.value, 10) || moq;
        const next = value - moq;
        this.qtyInput.value = next < moq ? moq : next;
        this.updateStepButtons();
      }

      onPlus(event) {
        event.preventDefault();
        const moq = this.currentMoq();
        const value = parseInt(this.qtyInput.value, 10) || moq;
        this.qtyInput.value = value + moq;
        this.updateStepButtons();
      }

      onQtyChange() {
        const moq = this.currentMoq();
        let value = parseInt(this.qtyInput.value, 10);
        if (!value || value < moq) {
          value = moq;
        } else {
          value = Math.round(value / moq) * moq;
          if (value < moq) value = moq;
        }
        this.qtyInput.value = value;
        this.updateStepButtons();
      }

      updateStepButtons() {
        if (!this.minusButton) return;
        const moq = this.currentMoq();
        const value = parseInt(this.qtyInput.value, 10) || moq;
        this.minusButton.classList.toggle('disabled', value <= moq);
        this.minusButton.toggleAttribute('disabled', value <= moq);
      }

      onVariantChange() {
        const variant = this.selectedVariant();

        if (variant) {
          this.idInput.value = variant.id;
          this.idInput.disabled = false;
          this.applyMoq(this.currentMoq());
          this.updateStepButtons();
          this.setSubmitEnabled(variant.available !== false);
          this.updatePill(this.currentMoq());
        } else {
          this.idInput.value = '';
          this.idInput.disabled = true;
          this.setSubmitEnabled(false);
          this.updatePill(null);
        }
      }

      updatePill(moq) {
        if (!this.moqPill) return;
        if (moq && moq > 1) {
          const template = this.moqPill.dataset.pillTemplate || 'ab [qty] Stk.';
          this.moqPill.textContent = template.replace('[qty]', moq);
          this.moqPill.classList.remove('hidden');
        } else {
          this.moqPill.textContent = '';
          this.moqPill.classList.add('hidden');
        }
      }

      setSubmitEnabled(enabled) {
        if (!this.submitButton) return;
        this.submitButton.toggleAttribute('disabled', !enabled);
        this.submitButton.classList.toggle('card-add--disabled', !enabled);
      }

      onSubmit(event) {
        event.preventDefault();

        if (this.submitButton.hasAttribute('disabled') || this.submitButton.getAttribute('aria-disabled') === 'true') {
          return;
        }

        this.submitButton.setAttribute('aria-disabled', 'true');
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner')?.classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'card-quantity-add',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            this.error = false;
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'card-quantity-add',
              productVariantId: formData.get('id'),
              cartData: response,
            });
            this.cart.renderContents(response);
            if (typeof this.cart.open === 'function') this.cart.open();
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner')?.classList.add('hidden');
          });
      }
    }
  );
}

if (!customElements.get('card-variant-dropdown')) {
  customElements.define(
    'card-variant-dropdown',
    class CardVariantDropdown extends HTMLElement {
      constructor() {
        super();
        this.native = this.querySelector('.card-variant__native');
        this.trigger = this.querySelector('.card-variant__trigger');
        this.valueEl = this.querySelector('.card-variant__value');
        this.panel = this.querySelector('.card-variant__panel');
        this.options = Array.from(this.querySelectorAll('.card-variant__option'));
        this.placeholder = this.valueEl ? this.valueEl.textContent : '';

        this.trigger?.addEventListener('click', this.toggle.bind(this));
        this.options.forEach((opt) => opt.addEventListener('click', this.onSelect.bind(this)));
        this.onDocClick = this.onDocClick.bind(this);
        this.onKeydown = this.onKeydown.bind(this);
      }

      toggle(event) {
        event.preventDefault();
        this.open ? this.close() : this.openMenu();
      }

      openMenu() {
        this.open = true;
        this.panel.hidden = false;
        this.trigger.setAttribute('aria-expanded', 'true');
        document.addEventListener('click', this.onDocClick);
        document.addEventListener('keydown', this.onKeydown);
      }

      close() {
        this.open = false;
        this.panel.hidden = true;
        this.trigger.setAttribute('aria-expanded', 'false');
        document.removeEventListener('click', this.onDocClick);
        document.removeEventListener('keydown', this.onKeydown);
      }

      onDocClick(event) {
        if (!this.contains(event.target)) this.close();
      }

      onKeydown(event) {
        if (event.key === 'Escape') {
          this.close();
          this.trigger.focus();
        }
      }

      onSelect(event) {
        const button = event.currentTarget;
        if (button.dataset.unavailable === 'true') return;

        const value = button.dataset.value;
        this.options.forEach((o) => o.classList.toggle('is-selected', o === button));
        if (this.valueEl) this.valueEl.textContent = button.textContent.trim();

        if (this.native && this.native.value !== value) {
          this.native.value = value;
          this.native.dispatchEvent(new Event('change', { bubbles: true }));
        }
        this.close();
        this.trigger.focus();
      }
    }
  );
}
